import { StandardProposalEntity } from "domain/entity/StandardProposal/StandardProposalEntity";
import { AclType } from "presentation/constant/ANAInfo/NbisivPermission";
import { Permission } from "presentation/constant/ANAInfo/PermissionName";
import { MessageConstant } from "presentation/constant/MessageConstant";
import { StandardProposalConstant } from "presentation/constant/StandardProposal/StandardProposalConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useStandardProposalVM } from "presentation/hook/StandardProposal/useStandardProposalVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useStandardProposalTracked } from "presentation/store/StandardProposal/StandardProposalProvider";
import { isPermissionExist } from "presentation/utils/permissionUtils";
import { ConfirmDeleteModal } from "presentation/view/components/ConfirmDeleteModal";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { HeaderWithBackButton } from "presentation/view/components/HeaderWithBackButton/HeaderWithBackButton";
import { NbisIconButton } from "presentation/view/components/NbisIconButton";
import { memo, useCallback, useState } from "react";
import { HPHBreadcrumb, HPHButton, IconButton, Loader } from "veronica-ui-component/dist/component/core";
import { Breadcrumb, Sidebarheader, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const StandardProposalTitleBar: React.FC = () => {
    const [standardProposalState] = useStandardProposalTracked();
    const standardProposalVM = useStandardProposalVM();
    const { isShowCopyPanel, isShowTariffItemTierPanel, isUpdateOrAdd, isEditStdProposal, selectedRows, searchCriteria, searchStatus } = standardProposalState;
    const STANDARD_PROPOSAL_CONSTANT = StandardProposalConstant.Title;
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { allowCreate, allowUpdate, allowDelete } = anaInfoState;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isShowDelete, setIsShowDelete] = useState<boolean>(false);
    const [deletedProposal, setDeletedProposal] = useState<StandardProposalEntity[]>();
    const allowUpdateReject = isPermissionExist(Permission.STANDARD_PROPOSAL_REJECT, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);
    const allowUpdateUnconfirm = isPermissionExist(Permission.STANDARD_PROPOSAL_UNCONFIRM, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);
    const allowUpdateApprove = isPermissionExist(Permission.STANDARD_PROPOSAL_APPROVE, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);

    const handleSearch = useCallback(() => {
        standardProposalVM.onSearchClick();
    }, [standardProposalVM]);

    const handleAdd = useCallback(() => {
        standardProposalVM.onAddClick();
    }, [standardProposalVM]);

    const handleReject = useCallback(async () => {
        if (selectedRows.length !== 1) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (!selectedRows.every(entity => entity.status === "FINALIZED")) {
            messageBarVM.showError("The selected proposal is not in \"FINALIZED\" state.");
            return;
        }
        standardProposalVM.onRejectClick();

    }, [messageBarVM, selectedRows, standardProposalVM]);

    const handleApprove = useCallback(async () => {
        if (anaInfoState.operatingUnit === 'SAPT' || anaInfoState.operatingUnit === 'KICT') {
            let isError = false
            selectedRows?.forEach(entity => {
                if (entity.submittedBy === anaInfoState.userName) {
                    messageBarVM.showError("Proposal cannot approve, because Submitted by is same as " + entity.submittedBy);
                    isError = true;
                    return;
                }
            });
            if (isError) return;
        }
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (!selectedRows.every(entity => entity.status === "FINALIZED")) {
            messageBarVM.showError("The selected proposal is not in \"FINALIZED\" state.");
            return;
        }
        standardProposalVM.onShowLoading();
        standardProposalVM.onApprove(selectedRows).then(async (data) => {
            if (data === "success") {
                messageBarVM.showSuccess("Approve successfully.");
                if (searchStatus !== "") {
                    await standardProposalVM.onGroupButtonClick(searchStatus);
                } else {
                    await standardProposalVM.searchStandardProposal(searchCriteria);
                }
                standardProposalVM.onHideLoading();
            } else {
                standardProposalVM.onHideLoading();
                messageBarVM.showError(data);
            }
            standardProposalVM.onHideLoading();
        }).catch(error => {
            standardProposalVM.onHideLoading();
        });
    }, [messageBarVM, searchCriteria, searchStatus, selectedRows, standardProposalVM, anaInfoState.userName, anaInfoState.operatingUnit]);


    const handleUnConfirm = useCallback(async () => {
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (!selectedRows.every(entity => entity.status === "FINALIZED" || entity.status === "PROPOSED")) {
            messageBarVM.showError("The selected proposal is not in \"FINALIZED\" state.");
            return;
        }
        standardProposalVM.onShowLoading();
        standardProposalVM.onUnconfirm(selectedRows).then(async (data) => {
            if (data === "success") {
                messageBarVM.showSuccess("Unconfirm Successfully.");
                if (searchStatus !== "") {
                    await standardProposalVM.onGroupButtonClick(searchStatus);
                } else {
                    await standardProposalVM.searchStandardProposal(searchCriteria);
                }
                standardProposalVM.onHideLoading();
            } else {
                standardProposalVM.onHideLoading();
                messageBarVM.showError(data);
            }
            standardProposalVM.onHideLoading();
        }).catch(error => {
            standardProposalVM.onHideLoading();
        });

    }, [messageBarVM, searchCriteria, searchStatus, selectedRows, standardProposalVM]);

    const handleRenew = useCallback(async () => {
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (!selectedRows.every(entity => entity.status === "FINALIZED" && entity.confirmedDate)) {
            messageBarVM.showError("The selected proposal can not Renew.");
            return;
        }
        standardProposalVM.onShowLoading();
        standardProposalVM.onRenew(selectedRows.map(entity => Number(entity.key))).then(async (data) => {
            if (data === "success") {
                messageBarVM.showSuccess("Renew successfully.");
                if (searchStatus !== "") {
                    await standardProposalVM.onGroupButtonClick(searchStatus);
                } else {
                    await standardProposalVM.searchStandardProposal(searchCriteria);
                }
                standardProposalVM.onHideLoading();
            } else {
                standardProposalVM.onHideLoading();
                messageBarVM.showError(data);
            }
            standardProposalVM.onHideLoading();
        }).catch(error => {
            standardProposalVM.onHideLoading();
        });

    }, [messageBarVM, searchCriteria, searchStatus, selectedRows, standardProposalVM]);

    const handleSubmit = useCallback(async () => {
        //isSTDProposalFinalizeApproved
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (selectedRows.every(entity => entity.status === "FINALIZED")) {
            messageBarVM.showError("Standard Proposal status is \"FINALIZED\", cannot Submit.");
            return;
        }
        standardProposalVM.onShowLoading();
        standardProposalVM.onSubmit(selectedRows).then(async (data) => {
            if (data === "success") {
                messageBarVM.showSuccess("Submit Successfully.");
                if (searchStatus !== "") {
                    await standardProposalVM.onGroupButtonClick(searchStatus);
                } else {
                    await standardProposalVM.searchStandardProposal(searchCriteria);
                }
                standardProposalVM.onHideLoading();
            } else {
                standardProposalVM.onHideLoading();
                messageBarVM.showError(data);
            }
            standardProposalVM.onHideLoading();
        }).catch(error => {
            standardProposalVM.onHideLoading();
        });

    }, [messageBarVM, searchCriteria, searchStatus, selectedRows, standardProposalVM]);

    const handleDeleteClick = useCallback(async () => {
        if (!selectedRows.every(entity => entity.status === "NEW" || entity.status === "REJECTED")) {
            messageBarVM.showError("Standard Proposal status is \"FINALIZED\", Cannot Delete.");
            await standardProposalVM.searchStandardProposal(searchCriteria).then(data => {
                setIsLoading(false);
            });
            return;
        }
        setIsShowDelete(true);
        setDeletedProposal(selectedRows);
    }, [messageBarVM, searchCriteria, selectedRows, standardProposalVM]);

    const handleCancel = () => {
        // setIsLoading(true);
        setIsShowDelete(false);
        // standardProposalVM.searchStandardProposal(searchCriteria).then(data => {
        //     setIsLoading(false);
        // });
    }

    const handleDelete = () => {
        setIsLoading(true);
        setIsShowDelete(false);
        if (!deletedProposal) return;

        standardProposalVM.onDeleteClick(deletedProposal).then((data) => {
            if (data && data === "success") {
                messageBarVM.showSuccess(MessageConstant.common.DELETED_DATA_SUCCESSFUL)
                standardProposalVM.searchStandardProposal(searchCriteria).then(data => {
                    setIsLoading(false);
                });
            } else {
                messageBarVM.showError(data);
            }
        }).catch((error) => {
            setIsLoading(false);
        }).finally(() => {
            setIsLoading(false);
        });
    }


    const isRenewDisable = () => {
        if (!isShowTariffItemTierPanel && !isShowCopyPanel && !isUpdateOrAdd && !isEditStdProposal && selectedRows.length > 0 && selectedRows.every(entity => entity.status === "FINALIZED" && entity.confirmedDate)) return false
        return true;
    }
    const isDeleteDisable = () => {
        if (!isShowTariffItemTierPanel && !isUpdateOrAdd && !isUpdateOrAdd && !isEditStdProposal && selectedRows.length > 0 && selectedRows.every(entity => entity.status === "NEW" || entity.status === "REJECTED")) return false
        return true;
    }
    const isSubmitDisable = () => {
        if (!isShowTariffItemTierPanel && !isUpdateOrAdd && !isUpdateOrAdd && !isEditStdProposal && selectedRows.length > 0 && selectedRows.every(entity => entity.status === "NEW" || entity.status === "REJECTED" || entity.status === "PROPOSED")) return false
        return true;
    }
    const isUnconfrimDisable = () => {
        if (!isShowTariffItemTierPanel && !isShowCopyPanel && !isUpdateOrAdd && !isEditStdProposal && selectedRows.length > 0 && selectedRows.every(entity => entity.status === "FINALIZED")) return false
        return true;
    }
    const isApproveDisable = () => {
        if (!isShowTariffItemTierPanel && !isUpdateOrAdd && !isEditStdProposal && selectedRows.length > 0 && selectedRows.every(entity => (entity.status === "FINALIZED") && !entity.confirmedDate)) return false
        return true;
    }

    const isRejectDisable = () => {
        if (!isShowTariffItemTierPanel && !isUpdateOrAdd && !isEditStdProposal && selectedRows.length > 0 && selectedRows.every(entity => (entity.status === "FINALIZED") && !entity.confirmedDate)) return false
        return true;
    }

    const isSearchDisable = () => {
        if (!isShowTariffItemTierPanel && !isEditStdProposal) return false
        return true;
    }

    return <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
        {isShowTariffItemTierPanel && <HeaderWithBackButton callback={standardProposalVM.onCloseClick}>
            <Breadcrumb>
                <HPHBreadcrumb breadcrumbData={[{ title: StandardProposalConstant.Agreement.AGREEMENT }, { title: STANDARD_PROPOSAL_CONSTANT.STANDARD_PROPOSAL }]} onCurrentClick={() => { }}></HPHBreadcrumb>
            </Breadcrumb>
        </HeaderWithBackButton>}
        {!isShowTariffItemTierPanel && <HeaderTitle>{STANDARD_PROPOSAL_CONSTANT.STANDARD_PROPOSAL}</HeaderTitle>}

        {(standardProposalState.isLoading || isLoading) && <Loader Indicator="Spinner" size="Medium" />}
        <StyledAction className="tm-animated-wrapper">
            <IconButton fileName='Icon-search' disabled={isSearchDisable()} size='medium' toolTipText={'Search'} toolTipArrow={false} onClick={handleSearch} />
            {(allowCreate) && <NbisIconButton fileName='Icon-add' disabled={isShowTariffItemTierPanel || isShowCopyPanel || isUpdateOrAdd || isEditStdProposal} size='medium' toolTipText={'Add'} onClick={handleAdd} />}
            {(allowCreate || allowDelete) && <div className="add-seperator" />}
            {(allowCreate) && <HPHButton disabled={isRenewDisable()} label={STANDARD_PROPOSAL_CONSTANT.RENEW_BUT} size={'Small'} theme={'Secondary'} onClick={handleRenew} />}
            {(allowDelete) && <HPHButton disabled={isDeleteDisable()} label={WorkspaceConstant.Common.BUTTON_DELETE} size={'Small'} theme={'Secondary'} onClick={handleDeleteClick} />}

            {(allowUpdate) && <>
                <div className="add-seperator" />
                {allowUpdateUnconfirm && <HPHButton disabled={isUnconfrimDisable()} label={WorkspaceConstant.Common.BUTTON_UNCONFIRM} size={'Small'} theme={'Secondary'} onClick={handleUnConfirm} />}
                <HPHButton disabled={isSubmitDisable()} label={STANDARD_PROPOSAL_CONSTANT.SUBMIT_BUT} size={'Small'} theme={allowUpdate ? 'Secondary' : 'Primary'} onClick={handleSubmit} />
                {(allowUpdateReject || allowUpdateApprove) && <div className="add-seperator" />}
                {allowUpdateReject && <HPHButton disabled={isRejectDisable()} label={STANDARD_PROPOSAL_CONSTANT.REJECT_BUT} size={'Small'} theme={'Secondary'} onClick={handleReject} />}
                {allowUpdateApprove && <HPHButton disabled={isApproveDisable()} label={STANDARD_PROPOSAL_CONSTANT.APPROVE_BUT} size={'Small'} theme={'Primary'} onClick={handleApprove} />}
            </>}
            {(allowDelete) && <ConfirmDeleteModal headerText={`${WorkspaceConstant.Common.BUTTON_DELETE} ${StandardProposalConstant.Title.STANDARD_PROPOSAL}`}
                contentText={`${MessageConstant.common.DELETE_ALERT_TITLE}\n${MessageConstant.common.DELETE_ALERT_MESSAGE}`}
                visible={isShowDelete}
                onCancelClick={handleCancel}
                onDeleteClick={handleDelete}
            />}
        </StyledAction>
        {standardProposalState.forceRefresh && <></>}
    </Sidebarheader>
}

export default memo(StandardProposalTitleBar);